'use client';

import { useState, useRef, useCallback, useEffect } from 'react';

interface UseAudioRecorderReturn {
  isRecording: boolean;
  isPaused: boolean;
  recordingTime: number;
  audioURL: string | null;
  audioBlob: Blob | null;
  startRecording: () => Promise<void>;
  stopRecording: () => void;
  pauseRecording: () => void;
  resumeRecording: () => void;
  clearRecording: () => void;
  waveformData: number[];
}

export const useAudioRecorder = (): UseAudioRecorderReturn => {
  const [isRecording, setIsRecording] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [recordingTime, setRecordingTime] = useState(0);
  const [audioURL, setAudioURL] = useState<string | null>(null);
  const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
  const [waveformData, setWaveformData] = useState<number[]>([]);

  const mediaRecorder = useRef<MediaRecorder | null>(null);
  const audioContext = useRef<AudioContext | null>(null);
  const analyser = useRef<AnalyserNode | null>(null);
  const dataArray = useRef<Uint8Array | null>(null);
  const animationFrame = useRef<number | null>(null);
  const stream = useRef<MediaStream | null>(null);
  const chunks = useRef<Blob[]>([]);
  const startTime = useRef<number>(0);
  const pausedTime = useRef<number>(0);
  const waveformBuffer = useRef<number[]>([]);
  const frameCount = useRef(0);

  const isRecordingRef = useRef(false);
  const isPausedRef = useRef(false);

  const updateWaveform = useCallback(() => {
    if (!analyser.current || !dataArray.current) {
      return;
    }

    // Create a new Uint8Array to ensure correct type
    const buffer = new Uint8Array(dataArray.current.length);
    analyser.current.getByteTimeDomainData(buffer);

    // Only update every 6 frames to slow down the animation
    frameCount.current++;
    if (frameCount.current % 6 === 0) {
      // Calculate RMS (Root Mean Square) for better amplitude detection
      let sum = 0;
      for (let i = 0; i < buffer.length; i++) {
        const value = (buffer[i] - 128) / 128.0; // Normalize to -1 to 1
        sum += value * value;
      }
      const rms = Math.sqrt(sum / buffer.length);

      // Scale up the RMS value for better visibility (0-128 range)
      const amplitude = Math.min(128, rms * 256);

      // Add to buffer (keep last 150 samples for scrolling effect)
      waveformBuffer.current.push(amplitude);
      if (waveformBuffer.current.length > 150) {
        waveformBuffer.current.shift();
      }

      setWaveformData([...waveformBuffer.current]);
    }

    if (isRecordingRef.current && !isPausedRef.current) {
      animationFrame.current = requestAnimationFrame(updateWaveform);
    }
  }, []);

  const updateTimer = useCallback(() => {
    if (isRecording && !isPaused) {
      setRecordingTime(Date.now() - startTime.current - pausedTime.current);
    }
  }, [isRecording, isPaused]);

  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (isRecording && !isPaused) {
      interval = setInterval(updateTimer, 100);
    }
    return () => clearInterval(interval);
  }, [isRecording, isPaused, updateTimer]);

  const startRecording = async () => {
    try {
      stream.current = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          sampleRate: 44100,
        }
      });

      // Set up audio analysis
      audioContext.current = new AudioContext();
      analyser.current = audioContext.current.createAnalyser();
      const source = audioContext.current.createMediaStreamSource(stream.current);
      source.connect(analyser.current);

      analyser.current.fftSize = 2048;
      const bufferLength = analyser.current.fftSize;
      dataArray.current = new Uint8Array(bufferLength);

      // Set up media recorder
      mediaRecorder.current = new MediaRecorder(stream.current, {
        mimeType: 'audio/webm',
      });

      chunks.current = [];
      mediaRecorder.current.ondataavailable = (event) => {
        chunks.current.push(event.data);
      };

      mediaRecorder.current.onstop = () => {
        const blob = new Blob(chunks.current, { type: 'audio/webm' });
        const url = URL.createObjectURL(blob);
        setAudioBlob(blob);
        setAudioURL(url);
      };

      mediaRecorder.current.start(100);
      setIsRecording(true);
      setIsPaused(false);
      isRecordingRef.current = true;
      isPausedRef.current = false;
      startTime.current = Date.now();
      pausedTime.current = 0;
      updateWaveform();
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.stop();
      setIsRecording(false);
      setIsPaused(false);
      isRecordingRef.current = false;
      isPausedRef.current = false;

      if (stream.current) {
        stream.current.getTracks().forEach(track => track.stop());
      }

      if (audioContext.current) {
        audioContext.current.close();
      }

      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    }
  };

  const pauseRecording = () => {
    if (mediaRecorder.current && isRecording) {
      mediaRecorder.current.pause();
      setIsPaused(true);
      isPausedRef.current = true;
      if (animationFrame.current) {
        cancelAnimationFrame(animationFrame.current);
      }
    }
  };

  const resumeRecording = () => {
    if (mediaRecorder.current && isRecording && isPaused) {
      mediaRecorder.current.resume();
      setIsPaused(false);
      isPausedRef.current = false;
      const pauseEndTime = Date.now();
      pausedTime.current += pauseEndTime - startTime.current - recordingTime;
      updateWaveform();
    }
  };

  const clearRecording = () => {
    if (audioURL) {
      URL.revokeObjectURL(audioURL);
    }
    setAudioURL(null);
    setAudioBlob(null);
    setRecordingTime(0);
    waveformBuffer.current = [];
    setWaveformData([]);
    chunks.current = [];
  };

  return {
    isRecording,
    isPaused,
    recordingTime,
    audioURL,
    audioBlob,
    startRecording,
    stopRecording,
    pauseRecording,
    resumeRecording,
    clearRecording,
    waveformData,
  };
};